import { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, Flex, IconButton, Text } from "@chakra-ui/react";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { MdNotificationsActive } from "react-icons/md";
import { useConditionalBackground } from "@/contexts/useConditionalBackground";
import ComoUtilizarModal, {
  type PassoComoUtilizar,
} from "@/components/consignado/Cobranca/VinculosAtivosRedesign/ComoUtilizarModal";
import ConfirmDialog from "@/components/consignado/Cobranca/components/ConfirmDialog";
import {
  CatalogoDisparo,
  CriarDisparoDto,
  DisparoConfiguracao,
  PreviewBuscaDto,
  PreviewResultado,
  PropriedadeCatalogo,
  apelidoDaPropriedade,
  apelidoDisponivel,
  filtroEstaCompleto,
  tokenVariavel,
} from "@/models/consignado/disparo.model";
import { condicoesDaArvore } from "./filtro-arvore";
import {
  DisparoDraft,
  DisparoDraftPatch,
  DisparoErros,
  draftDeDisparo,
  draftParaDto,
  draftVazio,
  gatilhoUsaFiltro,
  novaVariavel,
  semErros,
  validarContexto,
  validarDraft,
} from "./disparo-draft";
import DisparoForm from "./DisparoForm";
import { formatoPadrao } from "./catalogo.helpers";
import { SLIDE_FORMATOS } from "./FormatosComoUtilizar";
import { SLIDE_TESTE_TEMPLATE } from "./TesteTemplateComoUtilizar";
import { useSaidaProtegida } from "./useSaidaProtegida";
import DisparoView from "./DisparoView";
import VariaveisSidebar from "./VariaveisSidebar";
import { VariavelInsertContext, type InsertFn } from "./VariavelInsertContext";
import { FiltroActiveContext, type FiltroSelectFn } from "./FiltroActiveContext";
import {
  CONCEITOS_DISPARO,
  PASSOS_EDITAR_DISPARO,
  PASSOS_NOVO_DISPARO,
  PASSOS_VISUALIZAR_DISPARO,
} from "./comoUtilizar.content";

export type DisparoScreenMode = "create" | "edit" | "view";

type Props = {
  mode: DisparoScreenMode;
  disparo: DisparoConfiguracao | null;
  catalogo?: CatalogoDisparo;
  isSaving?: boolean;
  onBack: () => void;
  /**
   * Devolvendo a promessa do POST/PUT, o Salvar fica travado até ela
   * terminar — é o que impede o duplo clique de gravar dois disparos.
   */
  onSave: (dto: CriarDisparoDto) => void | Promise<unknown>;
  /** Salva e abre o envio de teste. Sem isso o botão "Testar template" não aparece. */
  onSaveAndTest?: (dto: CriarDisparoDto) => void | Promise<unknown>;
  onSwitchToEdit: () => void;
  /** POST /disparos/preview. Sem isso o "Testar busca" mostra amostra simulada. */
  onPreview?: (dto: PreviewBuscaDto) => Promise<PreviewResultado>;
};

const fieldFont = { fontFamily: "Lexend Deca" };

const TITULO = "Configurações de Notificações";

const SUBTITLES: Record<DisparoScreenMode, string> = {
  create: "Novo disparo",
  edit: "Editar disparo",
  view: "Detalhes do disparo",
};

const PASSOS_POR_MODO: Record<DisparoScreenMode, PassoComoUtilizar[]> = {
  create: PASSOS_NOVO_DISPARO,
  edit: PASSOS_EDITAR_DISPARO,
  view: PASSOS_VISUALIZAR_DISPARO,
};

/** Erro em algum campo de "Identificação do disparo" (cabeçalho, agendamento ou filtro). */
const erroNaIdentificacao = (e: DisparoErros): boolean =>
  Boolean(
    e.nome || e.evento || e.frequencia || e.intervalo || e.hora || e.diaSemana || e.diaMes || e.filtros,
  ) || Object.keys(e.porFiltro).length > 0;

// Tela cheia de criar/editar/visualizar um disparo. Storybook-only: a
// "navegação" entre lista e tela é simulada por troca de view no componente
// pai (NotificacoesConfigRedesign), sem react-router.
export default function DisparoScreen({
  mode,
  disparo,
  catalogo,
  isSaving,
  onBack,
  onSave,
  onSaveAndTest,
  onSwitchToEdit,
  onPreview,
}: Props) {
  const backgroundStyle = useConditionalBackground();
  const [draft, setDraft] = useState<DisparoDraft>(() =>
    mode !== "create" && disparo ? draftDeDisparo(disparo) : draftVazio(),
  );
  const [erros, setErros] = useState<DisparoErros>({ porFiltro: {}, porCanal: {} });

  // Colapso de "Identificação do disparo" — decide qual sessão a sidebar
  // mostra: aberta é a do disparo (campo de filtro), fechada é a da
  // mensagem (variáveis).
  //
  // Antes ela colapsava sozinha quando o primeiro canal entrava. Com canal
  // único o canal já nasce junto, então não há esse momento: num cadastro
  // novo ela começa aberta, porque é por onde se começa; abrindo um
  // existente começa fechada, porque o que se vem ver é a mensagem.
  const [identificacaoAberta, setIdentificacaoAberta] = useState(mode === "create");

  // Campo de mensagem em foco, e o canal dono dele — o mapa de variáveis é
  // por canal, então inserir precisa saber onde declarar a variável.
  const [activeInsert, setActiveInsert] = useState<{ canalId: string; insert: InsertFn } | null>(null);
  const registerActiveInsert = useCallback((canalId: string, insert: InsertFn) => {
    // Só registra o alvo. Não fecha a identificação: os campos de mensagem se
    // registram também ao montar (sem foco), e fechar aqui fazia o "Novo
    // disparo" abrir com a identificação já recolhida. Quem fecha é o foco em
    // "Canal e mensagem" (onEditarCanais no DisparoForm).
    setActiveInsert({ canalId, insert });
  }, []);

  // Alvo ativo pro "column picker" de campo de um filtro — some assim que o
  // campo é escolhido. `key` é o id do filtro, pra que remover um filtro só
  // limpe o alvo se ele for o dono.
  const [activeFiltroTarget, setActiveFiltroTarget] = useState<{
    key: string;
    onSelect: FiltroSelectFn;
  } | null>(null);
  const registerActiveFiltro = useCallback(
    (key: string, onSelect: FiltroSelectFn) => setActiveFiltroTarget({ key, onSelect }),
    [],
  );
  const clearActiveFiltro = useCallback(
    (key: string) => setActiveFiltroTarget((prev) => (prev?.key === key ? null : prev)),
    [],
  );

  // Ligado na primeira tentativa de salvar: a partir daí o formulário
  // revalida a cada alteração. Sem isso a mensagem fica pregada no campo
  // depois de corrigida — "Selecione o evento." continuava vermelho com o
  // evento já escolhido, porque a validação só rodava no clique de Salvar.
  const [revalidando, setRevalidando] = useState(false);

  // Condições que existiam na última tentativa de salvar. Uma criada depois
  // (o "+" já abre o seletor de campo) não é cobrada enquanto está pela
  // metade — o erro dela viria antes de o usuário escolher o campo. Quem
  // barra abandoná-la é o requestOpenFiltroId do DisparoForm; o próximo
  // Salvar a cobra normalmente.
  const condicoesNoSalvar = useRef<Set<string>>(new Set());

  const saida = useSaidaProtegida({ draft, ativo: mode !== "view", onBack });

  useEffect(() => {
    setErros({ porFiltro: {}, porCanal: {} });
    setRevalidando(false);
    setIdentificacaoAberta(mode === "create");
    saida.zerarReferencia();
    if (mode === "create") setDraft(draftVazio());
    else if (disparo) setDraft(draftDeDisparo(disparo));
  }, [mode, disparo]);

  useEffect(() => {
    if (!revalidando) return;
    const v = validarDraft(draft, catalogo);
    condicoesDaArvore(draft.filtros).forEach((f) => {
      if (!condicoesNoSalvar.current.has(f.id) && (!f.propriedade || !filtroEstaCompleto(f))) {
        delete v.porFiltro[f.id];
      }
    });
    setErros(v);
  }, [draft, catalogo, revalidando]);

  const handleChange = (patch: DisparoDraftPatch) =>
    setDraft((prev) => ({ ...prev, ...(typeof patch === "function" ? patch(prev) : patch) }));

  /**
   * Trocar o contexto zera o filtro e tira as variáveis, mas não apaga o
   * contato (ver handleContexto no DisparoForm) — o que não existir no contexto novo
   * precisa aparecer na hora, e não só quando alguém tentar salvar. Aponta só
   * o que o contexto invalidou no que já estava preenchido (validarContexto):
   * rodar a validação inteira aqui acusava evento, campo de filtro e provedor
   * que o usuário ainda nem tinha escolhido. Depois da primeira tentativa de
   * salvar, quem cuida é a revalidação completa acima.
   */
  const contextoAnterior = useRef(draft.contexto);
  useEffect(() => {
    if (contextoAnterior.current === draft.contexto) return;
    contextoAnterior.current = draft.contexto;
    if (revalidando) return;
    setErros((prev) => ({ ...prev, ...validarContexto(draft, catalogo) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.contexto]);

  // O "Evento incompatível" que a troca de contexto acende sai quando o
  // evento muda — o Select só oferece eventos do contexto atual.
  const eventoAnterior = useRef(draft.evento);
  useEffect(() => {
    if (eventoAnterior.current === draft.evento) return;
    eventoAnterior.current = draft.evento;
    if (revalidando) return;
    setErros((prev) => ({ ...prev, evento: undefined }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.evento]);

  // Trava do Salvar. O `isSaving` sozinho não basta: ele só chega no render
  // seguinte ao primeiro clique, e um segundo clique antes disso gravava um
  // disparo igual (dois POSTs, os dois 201). A ref trava na hora.
  const salvando = useRef(false);
  const formTopo = useRef<HTMLDivElement>(null);
  // Qual dos dois botões disparou o salvar — é ele que mostra o carregando.
  const [acaoEmCurso, setAcaoEmCurso] = useState<"salvar" | "testar">("salvar");

  const handleSubmit = (acao: "salvar" | "testar" = "salvar") => {
    if (salvando.current) return;
    const validacao = validarDraft(draft, catalogo);
    condicoesNoSalvar.current = new Set(condicoesDaArvore(draft.filtros).map((f) => f.id));
    setErros(validacao);
    setRevalidando(true);
    if (!semErros(validacao)) {
      // Preencher o canal recolhe a identificação; um erro de nome ou de
      // filtro lá dentro ficava invisível e o Salvar parecia não fazer nada.
      if (erroNaIdentificacao(validacao)) {
        setIdentificacaoAberta(true);
        requestAnimationFrame(() =>
          formTopo.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
        );
      }
      return;
    }
    salvando.current = true;
    setAcaoEmCurso(acao);
    const gravar = acao === "testar" && onSaveAndTest ? onSaveAndTest : onSave;
    Promise.resolve(gravar(draftParaDto(draft))).finally(() => {
      salvando.current = false;
    });
  };

  /**
   * Clique numa propriedade da sidebar com um campo de texto em foco:
   * declara a variável no canal dono do campo e insere `{{apelido}}` no
   * texto. O apelido é derivado do caminho (`tomador.nome` → `tomadorNome`)
   * porque o backend não aceita ponto na chave.
   */
  const handleSelecionarVariavel = (p: PropriedadeCatalogo) => {
    if (!activeInsert) return;
    const canal = draft.canais.find((c) => c.id === activeInsert.canalId);
    // Mensagem vinculada a template é só leitura: o backend a substitui pela
    // do template ao salvar (ver TemplateReutilizavelCampo).
    if (!canal || canal.templateId) return;

    // Propriedade já mapeada neste canal reusa o apelido — não faz sentido
    // ter duas variáveis apontando pro mesmo campo com o mesmo formato.
    const existente = canal.variaveis.find(
      (v) => v.origem === "PROPRIEDADE" && v.propriedade === p.id,
    );
    if (existente) {
      activeInsert.insert(tokenVariavel(existente.apelido));
      return;
    }

    const apelido = apelidoDisponivel(
      apelidoDaPropriedade(p.id),
      canal.variaveis.map((v) => v.apelido),
    );
    const variavel = novaVariavel(apelido, p.id, formatoPadrao(p));
    // As duas escritas caem no mesmo tick — a daqui e a que `insert` provoca
    // ao trocar o texto do canal. Ambas na forma funcional, compõem em vez de
    // uma sobrescrever `canais` da outra (era assim que o token entrava no
    // texto e a variável sumia do mapa, virando "sem mapeamento").
    handleChange((prev) => ({
      canais: prev.canais.map((c) =>
        c.id === canal.id ? { ...c, variaveis: [...c.variaveis, variavel] } : c,
      ),
    }));
    activeInsert.insert(tokenVariavel(apelido));
  };

  const isView = mode === "view";
  // Evento não tem filtro: na identificação a sidebar não tem o que oferecer e
  // some; ela só abre na sessão da mensagem.
  const sidebarSemUso = identificacaoAberta && !gatilhoUsaFiltro(draft.tipoGatilho);
  const mostrarSidebar =
    !isView && !sidebarSemUso && (draft.canais.length > 0 || draft.filtros.itens.length > 0);

  return (
    <VariavelInsertContext.Provider value={{ registerActiveInsert }}>
      <FiltroActiveContext.Provider
        value={{ registerActiveFiltro, clearActiveFiltro, activeKey: activeFiltroTarget?.key ?? null }}
      >
        <Box
          w="100%"
          {...saida.capturas}
        >
          {/* Header ocupa a largura inteira — a sidebar (quando aparece)
              fica abaixo dele, nunca ao lado/por cima. */}
          <Box px={{ base: "2", md: "4" }} pt={{ base: "2", md: "4" }}>
            <Box
              {...backgroundStyle}
              borderRadius="md"
              p={{ base: "4", md: "6" }}
              color="white"
              boxShadow="md"
              width="100%"
            >
              <Flex align="center" justify="space-between">
                <Flex align="center">
                  <IconButton
                    aria-label="Voltar"
                    icon={<ArrowBackIcon />}
                    variant="ghost"
                    color="white"
                    _hover={{ bg: "whiteAlpha.300" }}
                    onClick={saida.pedirSaida}
                    mr={3}
                  />
                  <MdNotificationsActive size="2rem" />
                  <Box ml={3}>
                    <Text fontSize={{ base: "2xl", md: "3xl" }} fontWeight="bold" fontFamily="Lexend Deca">
                      {TITULO}
                    </Text>
                    <Text fontSize="sm" opacity={0.85} fontFamily="Lexend Deca">
                      {SUBTITLES[mode]}
                    </Text>
                  </Box>
                </Flex>

                <ComoUtilizarModal
                  tituloTela={SUBTITLES[mode]}
                  passos={PASSOS_POR_MODO[mode]}
                  conceitos={CONCEITOS_DISPARO}
                  slidesExtras={mode === "view" ? [SLIDE_FORMATOS] : [SLIDE_FORMATOS, SLIDE_TESTE_TEMPLATE]}
                  maxColunas={mode === "view" ? 3 : 2}
                />
              </Flex>
            </Box>
          </Box>

          {/* Abaixo de lg a sidebar desce para baixo do formulário (ver
              VariaveisSidebar) — ao lado, com 380px fixos, ela tomava a tela. */}
          <Flex w="100%" align="stretch" direction={{ base: "column", lg: "row" }}>
            <Box flex={1} minW={0} px={{ base: "2", md: "4" }} pb={{ base: "2", md: "4" }} pt={6}>
              <Box
                ref={formTopo}
                scrollMarginTop={4}
                w="100%"
                maxW="1200px"
                mx="auto"
                bg="white"
                borderRadius="xl"
                boxShadow="sm"
                p={{ base: 4, md: 6 }}
              >
                {isView && disparo ? (
                  <DisparoView disparo={disparo} catalogo={catalogo} />
                ) : (
                  <DisparoForm
                    value={draft}
                    erros={erros}
                    catalogo={catalogo}
                    onChange={handleChange}
                    identificacaoAberta={identificacaoAberta}
                    onToggleIdentificacao={() => setIdentificacaoAberta((v) => !v)}
                    // A sidebar mostra uma sessão por vez (campo de filtro OU
                    // variável); com a identificação aberta as variáveis não
                    // aparecem, então entrar nos canais a recolhe.
                    onEditarCanais={() => setIdentificacaoAberta(false)}
                    onInserirToken={(token) => activeInsert?.insert(token)}
                    onPreview={onPreview}
                  />
                )}

                <Flex justify="flex-end" gap={3} mt={8} pt={4} borderTopWidth="1px" borderColor="gray.100">
                  <Button variant="ghost" onClick={saida.pedirSaida} {...fieldFont}>
                    {isView ? "Voltar" : "Cancelar"}
                  </Button>
                  {isView ? (
                    <Button colorScheme="green" onClick={onSwitchToEdit} {...fieldFont}>
                      Editar
                    </Button>
                  ) : (
                    <>
                      {onSaveAndTest && (
                        <Button
                          variant="outline"
                          colorScheme="green"
                          onClick={() => handleSubmit("testar")}
                          isLoading={isSaving && acaoEmCurso === "testar"}
                          isDisabled={isSaving && acaoEmCurso !== "testar"}
                          {...fieldFont}
                        >
                          Testar template
                        </Button>
                      )}
                      <Button
                        colorScheme="green"
                        onClick={() => handleSubmit("salvar")}
                        isLoading={isSaving && acaoEmCurso === "salvar"}
                        isDisabled={isSaving && acaoEmCurso !== "salvar"}
                        {...fieldFont}
                      >
                        Salvar
                      </Button>
                    </>
                  )}
                </Flex>
              </Box>
            </Box>

            {mostrarSidebar && (
              <VariaveisSidebar
                contexto={draft.contexto}
                catalogo={catalogo}
                disparoAberto={identificacaoAberta}
                semFiltro={!gatilhoUsaFiltro(draft.tipoGatilho)}
                hasActiveFiltroTarget={!!activeFiltroTarget}
                onSelectFiltroCampo={(p) => {
                  activeFiltroTarget?.onSelect(p);
                  setActiveFiltroTarget(null);
                }}
                hasActiveTarget={!!activeInsert}
                onSelectVariavel={handleSelecionarVariavel}
                mensagemVinculada={
                  !!draft.canais.find((c) => c.id === activeInsert?.canalId)?.templateId
                }
                // Do canal dono do campo em foco. Variável cujo token saiu do
                // texto já foi descartada (limparVariaveisOrfas), então a
                // lista é exatamente o que está na mensagem.
                variaveisUsadas={draft.canais
                  .find((c) => c.id === activeInsert?.canalId)
                  ?.variaveis.filter((v) => v.origem === "PROPRIEDADE")
                  .map((v) => v.propriedade)}
              />
            )}
          </Flex>

          <ConfirmDialog
            {...saida.dialogo}
            titulo="Sair sem salvar?"
            descricao="As alterações deste disparo ainda não foram salvas e serão perdidas."
            confirmLabel="Sair sem salvar"
            cancelLabel="Continuar editando"
          />
        </Box>
      </FiltroActiveContext.Provider>
    </VariavelInsertContext.Provider>
  );
}
